import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { brl } from "@/lib/format";
import { CheckCircle2, XCircle, Loader2, RefreshCw, Save, AlertTriangle, Plus, Pencil, EyeOff, Eye, DownloadCloud, Clock, CalendarDays, Trash2 } from "@/components/icons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const INPUT = "h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const LABEL = "block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1";
const CARD = "bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]";

const _hoje = () => new Date().toISOString().slice(0, 10);
const _mesRef = () => new Date().toISOString().slice(0, 7);

function ErroBox({ erro, testid }) {
  if (!erro) return null;
  return (
    <div data-testid={testid} className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm flex items-start gap-2">
      <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="break-words">{erro}</div>
    </div>
  );
}

export default function AjustesSankhya() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [vinculos, setVinculos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [ref, setRef] = useState(_mesRef());
  const [mesResult, setMesResult] = useState(null);
  const [mesErro, setMesErro] = useState(null);
  const [buscandoMes, setBuscandoMes] = useState(false);

  const [busca, setBusca] = useState({ codparc: "", cnpj: "", de: `${_mesRef()}-01`, ate: _hoje() });
  const [cliResult, setCliResult] = useState(null);
  const [cliErro, setCliErro] = useState(null);
  const [buscandoCli, setBuscandoCli] = useState(false);

  const [vendResult, setVendResult] = useState(null);
  const [vendErro, setVendErro] = useState(null);
  const [buscandoVend, setBuscandoVend] = useState(false);

  const [novoVend, setNovoVend] = useState(null); // {open, codvend, nome}
  const [editVend, setEditVend] = useState(null); // {codvend, nome_sankhya}
  const [meta, setMeta] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  const carregar = async () => {
    try {
      const [s, c, v, m] = await Promise.all([
        api.get("/sankhya/status"),
        api.get("/sankhya/config"),
        api.get("/sankhya/vinculos"),
        api.get("/sankhya/sync/meta"),
      ]);
      setStatus(s.data);
      setCfg(c.data);
      setVinculos(v.data?.vinculos || []);
      setUsuarios(v.data?.usuarios || []);
      setMeta(m.data || {});
    } catch (e) { toast.error(errMsg(e)); }
  };

  const atualizarAgora = async () => {
    setSincronizando(true);
    try {
      const { data } = await api.post("/sankhya/sync");
      if (data && data.ok === false) toast.error(data.erro || "Falha na sincronização.");
      else if (data?.em_andamento) toast.info(data.mensagem || "Já há uma sincronização em andamento.");
      else toast.success("Sincronização iniciada em segundo plano.");
      await carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSincronizando(false); }
  };

  const cargaInicial = async () => {
    if (!window.confirm("Carregar histórico completo (jan/2025 até hoje)? Roda em segundo plano — você pode fechar a página.")) return;
    setSincronizando(true);
    try {
      const { data } = await api.post("/sankhya/sync/inicial");
      if (data && data.ok === false) toast.error(data.erro || "Falha na carga inicial.");
      else if (data?.em_andamento) toast.info(data.mensagem || "Já há uma sincronização em andamento.");
      else toast.success("Carga iniciada. Acompanhe o progresso abaixo.");
      await carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSincronizando(false); }
  };

  const alternarLiberacao = async (novo) => {
    try {
      await api.put("/sankhya/config", {
        tops_venda: cfg.tops_venda,
        tops_devolucao: cfg.tops_devolucao,
        status_field: cfg.status_field,
        status_aprovado: cfg.status_aprovado,
        liberar_faturamento_equipe: novo,
      });
      setStatus((s) => ({ ...s, liberar_faturamento_equipe: novo }));
      toast.success(novo ? "Faturamento liberado para a equipe." : "Faturamento restrito a Administrador e Diretor Geral.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Polling do progresso enquanto há sync em andamento
  useEffect(() => {
    if (!meta?.progresso?.em_andamento) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/sankhya/sync/meta");
        setMeta(data || {});
        if (!data?.progresso?.em_andamento) clearInterval(t);
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [meta?.progresso?.em_andamento]);

  useEffect(() => { carregar(); }, []);

  const salvarCfg = async () => {
    setSaving(true);
    try {
      const payload = {
        tops_venda: cfg.tops_venda,
        tops_devolucao: cfg.tops_devolucao,
        status_field: cfg.status_field,
        status_aprovado: cfg.status_aprovado,
      };
      const { data } = await api.put("/sankhya/config", payload);
      setCfg({ ...cfg, ...data });
      toast.success("Regras salvas");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const testar = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/sankhya/testar-conexao");
      setTestResult(data);
    } catch (e) { setTestResult({ conectado: false, mensagem: errMsg(e) }); }
    finally { setTesting(false); }
  };

  const salvarVinculo = async (codvend, user_id) => {
    try {
      await api.put(`/sankhya/vinculos/${codvend}`, { user_id: user_id || null });
      await carregar();
      toast.success("Vínculo salvo");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const criarVendedor = async () => {
    if (!novoVend?.codvend || !novoVend?.nome?.trim()) {
      toast.error("Informe CODVEND e nome"); return;
    }
    try {
      await api.post("/sankhya/vendedores", {
        codvend: parseInt(novoVend.codvend, 10),
        nome_sankhya: novoVend.nome.trim(),
      });
      setNovoVend(null);
      await carregar();
      toast.success("Vendedor adicionado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const salvarNome = async () => {
    if (!editVend?.nome_sankhya?.trim()) { toast.error("Nome obrigatório"); return; }
    try {
      await api.patch(`/sankhya/vendedores/${editVend.codvend}`, { nome_sankhya: editVend.nome_sankhya.trim() });
      setEditVend(null);
      await carregar();
      toast.success("Nome atualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleAtivo = async (v) => {
    try {
      await api.patch(`/sankhya/vendedores/${v.codvend}`, { ativo: !v.ativo });
      await carregar();
      toast.success(v.ativo ? "Vendedor desativado" : "Vendedor reativado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const buscarMes = async () => {
    setBuscandoMes(true); setMesResult(null); setMesErro(null);
    try {
      const { data } = await api.get("/sankhya/faturamento-mes", { params: { ref } });
      if (data && data.ok === false) setMesErro(data.erro || "Falha na consulta.");
      else setMesResult(data);
    } catch (e) { setMesErro(errMsg(e)); }
    finally { setBuscandoMes(false); }
  };

  const buscarCliente = async () => {
    setBuscandoCli(true); setCliResult(null); setCliErro(null);
    try {
      const params = { de: busca.de, ate: busca.ate };
      if (busca.codparc) params.codparc = busca.codparc;
      else if (busca.cnpj) params.cnpj = busca.cnpj;
      else { toast.error("Informe CODPARC ou CNPJ"); setBuscandoCli(false); return; }
      const { data } = await api.get("/sankhya/faturamento-cliente", { params });
      if (data && data.ok === false) setCliErro(data.erro || "Falha na consulta.");
      else setCliResult(data);
    } catch (e) { setCliErro(errMsg(e)); }
    finally { setBuscandoCli(false); }
  };

  const buscarPorVend = async () => {
    setBuscandoVend(true); setVendResult(null); setVendErro(null);
    try {
      const { data } = await api.get("/sankhya/faturamento-por-codvend", { params: { ref } });
      if (data && data.ok === false) setVendErro(data.erro || "Falha na consulta.");
      else setVendResult(data);
    } catch (e) { setVendErro(errMsg(e)); }
    finally { setBuscandoVend(false); }
  };

  if (!status || !cfg) return <Loading />;

  const nao_config = !status.configurado;
  const usuariosOrdenados = [...usuarios].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader title="Integração Sankhya" subtitle="Tela de teste · Administrador / Diretor Geral" />

      {nao_config && (
        <div data-testid="sankhya-nao-config" className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>Credenciais do Sankhya não configuradas neste ambiente. As consultas ficam desativadas até a publicação com os segredos preenchidos.</div>
        </div>
      )}

      {/* Status + testar conexão */}
      <section className={CARD} data-testid="sankhya-conexao">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-navy">Conexão</div>
            {testResult ? (
              <div className={`mt-1 flex items-start gap-1.5 text-sm ${testResult.conectado ? "text-emerald-700" : "text-red-700"}`}>
                {testResult.conectado ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
                <span className="break-words">{testResult.mensagem}</span>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-1">{status.ambiente_mensagem || "Aguardando teste."}</div>
            )}
          </div>
          <button onClick={testar} disabled={testing || nao_config}
            data-testid="btn-testar-conexao"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Testar conexão
          </button>
        </div>
      </section>

      {/* Chave de liberação para a equipe */}
      <section className={CARD} data-testid="sankhya-liberacao">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-navy">Liberar faturamento para a equipe</div>
            <div className="text-xs text-muted-foreground mt-1 max-w-md">
              Desligada, só Administrador e Diretor Geral veem o menu, os cards da Início e os relatórios de faturamento. Ligada, cada perfil vê o próprio recorte da hierarquia.
            </div>
          </div>
          <button onClick={() => alternarLiberacao(!status.liberar_faturamento_equipe)}
            data-testid="btn-liberar-fat"
            aria-pressed={!!status.liberar_faturamento_equipe}
            className={"relative h-7 w-12 rounded-full transition-colors " + (status.liberar_faturamento_equipe ? "bg-emerald-500" : "bg-slate-300")}>
            <span className={"absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all " + (status.liberar_faturamento_equipe ? "left-5" : "left-0.5")} />
          </button>
        </div>
      </section>

      {/* Sincronização */}
      <section className={CARD} data-testid="sankhya-sync">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-navy flex items-center gap-2">
              <DownloadCloud className="w-4 h-4 text-brand" /> Sincronização
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {meta?.ultima_ok?.atualizado_em_local
                ? `Última atualização OK: ${new Date(meta.ultima_ok.atualizado_em_local).toLocaleString("pt-BR")}`
                : "Ainda sem sincronização bem-sucedida"}
            </div>
            {meta?.progresso?.em_andamento && (
              <div className="mt-2 text-xs text-brand-hover" data-testid="sync-progresso">
                <b>Em andamento:</b> {meta.progresso.escopo} · {meta.progresso.processados} de {meta.progresso.total} meses (mês {meta.progresso.mes_atual})
                <div className="w-64 bg-slate-100 rounded-full h-1.5 mt-1 overflow-hidden">
                  <div className="bg-brand h-1.5 rounded-full transition-all" style={{ width: `${(meta.progresso.processados / (meta.progresso.total || 1)) * 100}%` }} />
                </div>
              </div>
            )}
            {meta?.ultimo && meta.ultimo.ok === false && !meta?.progresso?.em_andamento && (
              <div className="text-xs text-red-700 mt-2" data-testid="sync-erro">
                <b>Última tentativa falhou:</b> {(meta.ultimo.erros || []).slice(0, 3).join(" · ") || "sem detalhes"}
              </div>
            )}
            {meta?.ultimo?.ok && !meta?.progresso?.em_andamento && meta.ultimo.meses_total > 0 && (
              <div className="text-xs text-emerald-700 mt-1">
                Concluída às {new Date(meta.ultimo.atualizado_em_local).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {meta.ultimo.linhas} linhas em {meta.ultimo.meses_ok}/{meta.ultimo.meses_total} meses.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cargaInicial} disabled={sincronizando || nao_config || !!meta?.progresso?.em_andamento}
              data-testid="btn-carga-inicial"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-input text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Carga inicial (jan/2025)
            </button>
            <button onClick={atualizarAgora} disabled={sincronizando || nao_config || !!meta?.progresso?.em_andamento}
              data-testid="btn-atualizar-agora"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
              {sincronizando ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
              Atualizar agora
            </button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Sincronização automática a cada 5 minutos, das 7h às 19h de segunda a sábado, mais uma carga às 23h que recalcula os 3 meses anteriores. Roda em segundo plano — pode fechar a página.
        </p>
        {cfg?.sync_modo_degradado && (
          <p className="text-[11px] text-warn mt-2 flex items-center gap-1" data-testid="sync-modo-degradado">
            <AlertTriangle className="w-3 h-3" /> Modo degradado: 3 execuções seguidas falharam, voltamos a sincronizar a cada 1h. Vai voltar sozinho ao normal assim que uma execução der certo.
          </p>
        )}
      </section>

      {/* Regras */}
      <section className={CARD} data-testid="sankhya-regras">
        <h3 className="font-semibold text-navy mb-3">Regras de faturamento</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>TOPs de venda faturada (CODTIPOPER)</label>
            <input value={cfg.tops_venda.join(", ")} onChange={(e) => setCfg({ ...cfg, tops_venda: e.target.value.split(/[,\s]+/).map(x => parseInt(x, 10)).filter(Boolean) })} className={INPUT + " w-full"} data-testid="cfg-tops-venda" />
          </div>
          <div>
            <label className={LABEL}>TOPs de devolução (CODTIPOPER)</label>
            <input value={cfg.tops_devolucao.join(", ")} onChange={(e) => setCfg({ ...cfg, tops_devolucao: e.target.value.split(/[,\s]+/).map(x => parseInt(x, 10)).filter(Boolean) })} className={INPUT + " w-full"} data-testid="cfg-tops-dev" />
          </div>
          <div>
            <label className={LABEL}>Campo de status</label>
            <select value={cfg.status_field} onChange={(e) => setCfg({ ...cfg, status_field: e.target.value })} className={INPUT + " w-full"} data-testid="cfg-status-field">
              {(cfg.status_field_opcoes || ["STATUSNFE"]).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Valor de "aprovado"</label>
            <input value={cfg.status_aprovado} onChange={(e) => setCfg({ ...cfg, status_aprovado: e.target.value })} className={INPUT + " w-full"} data-testid="cfg-status-val" />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={salvarCfg} disabled={saving}
            data-testid="btn-salvar-cfg"
            className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar regras
          </button>
        </div>
      </section>

      <Feriados />

      {/* Faturamento do mês */}
      <section className={CARD} data-testid="sankhya-mes">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h3 className="font-semibold text-navy flex-1">Faturamento por mês</h3>
          <input type="month" value={ref} onChange={(e) => setRef(e.target.value)} className={INPUT} data-testid="mes-ref" />
          <button onClick={buscarMes} disabled={buscandoMes || nao_config}
            data-testid="btn-buscar-mes"
            className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
            {buscandoMes ? <Loader2 className="w-4 h-4 animate-spin" /> : "Consultar"}
          </button>
        </div>
        <ErroBox erro={mesErro} testid="mes-erro" />
        {mesResult && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <ResBox rot="Vendas" v={mesResult.vendas.valor} n={mesResult.vendas.num_notas} />
              <ResBox rot="Devoluções" v={mesResult.devolucoes.valor} n={mesResult.devolucoes.num_notas} />
              <ResBox rot="Líquido" v={mesResult.liquido.valor} destaque />
            </div>
            <div className="text-xs text-muted-foreground">
              Notas excluídas por status: <b>{mesResult.excluidas_por_status}</b> · Devoluções sem data de faturamento: <b>{mesResult.devolucoes_sem_data}</b>
            </div>
            <TabelaTop rows={mesResult.por_top} />
          </div>
        )}
      </section>

      {/* Faturamento do cliente */}
      <section className={CARD} data-testid="sankhya-cliente">
        <h3 className="font-semibold text-navy mb-3">Faturamento por cliente</h3>
        <div className="grid md:grid-cols-5 gap-3 mb-3">
          <div><label className={LABEL}>CODPARC</label><input value={busca.codparc} onChange={(e) => setBusca({ ...busca, codparc: e.target.value, cnpj: "" })} className={INPUT + " w-full"} data-testid="cli-codparc" /></div>
          <div><label className={LABEL}>ou CNPJ</label><input value={busca.cnpj} onChange={(e) => setBusca({ ...busca, cnpj: e.target.value, codparc: "" })} className={INPUT + " w-full"} data-testid="cli-cnpj" /></div>
          <div><label className={LABEL}>De</label><input type="date" value={busca.de} onChange={(e) => setBusca({ ...busca, de: e.target.value })} className={INPUT + " w-full"} data-testid="cli-de" /></div>
          <div><label className={LABEL}>Até</label><input type="date" value={busca.ate} onChange={(e) => setBusca({ ...busca, ate: e.target.value })} className={INPUT + " w-full"} data-testid="cli-ate" /></div>
          <div className="flex items-end">
            <button onClick={buscarCliente} disabled={buscandoCli || nao_config}
              data-testid="btn-buscar-cliente"
              className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
              {buscandoCli ? <Loader2 className="w-4 h-4 animate-spin" /> : "Consultar"}
            </button>
          </div>
        </div>
        <ErroBox erro={cliErro} testid="cli-erro" />
        {cliResult && (
          <div className="grid grid-cols-3 gap-3">
            <ResBox rot="Vendas" v={cliResult.vendas.valor} n={cliResult.vendas.num_notas} />
            <ResBox rot="Devoluções" v={cliResult.devolucoes.valor} n={cliResult.devolucoes.num_notas} />
            <ResBox rot="Líquido" v={cliResult.liquido.valor} destaque />
          </div>
        )}
      </section>

      {/* Faturamento por CODVEND */}
      <section className={CARD} data-testid="sankhya-por-codvend">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h3 className="font-semibold text-navy flex-1">Faturamento por CODVEND · {ref}</h3>
          <button onClick={buscarPorVend} disabled={buscandoVend || nao_config}
            data-testid="btn-buscar-vend"
            className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
            {buscandoVend ? <Loader2 className="w-4 h-4 animate-spin" /> : "Consultar"}
          </button>
        </div>
        <ErroBox erro={vendErro} testid="vend-erro" />
        {vendResult && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left py-2">CODVEND</th>
                  <th className="text-left py-2">Nome Sankhya</th>
                  <th className="text-left py-2">Usuário vinculado</th>
                  <th className="text-right py-2">Vendas</th>
                  <th className="text-right py-2">Devoluções</th>
                  <th className="text-right py-2">Líquido</th>
                </tr>
              </thead>
              <tbody>
                {vendResult.itens.map((it) => (
                  <tr key={it.codvend} className="border-t border-black/5">
                    <td className="py-2 font-medium">{it.codvend}</td>
                    <td className="py-2">{it.nome_sankhya}</td>
                    <td className="py-2 text-muted-foreground">{it.user_vinculado || "—"}</td>
                    <td className="py-2 text-right">{brl(it.vendas)}</td>
                    <td className="py-2 text-right">{brl(it.devolucoes)}</td>
                    <td className="py-2 text-right font-semibold text-navy">{brl(it.liquido)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Vínculos */}
      <section className={CARD} data-testid="sankhya-vinculos">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-semibold text-navy">Vínculo vendedor × CODVEND</h3>
          <button onClick={() => setNovoVend({ codvend: "", nome: "" })}
            data-testid="btn-add-vendedor"
            className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover">
            <Plus className="w-4 h-4" /> Adicionar vendedor do Sankhya
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left py-2 w-20">CODVEND</th>
                <th className="text-left py-2">Nome Sankhya</th>
                <th className="text-left py-2">Usuário MPires Connect</th>
                <th className="text-right py-2 w-40">Ações</th>
              </tr>
            </thead>
            <tbody>
              {vinculos.map((v) => {
                const sug = v.sugestao;
                const inativo = v.ativo === false;
                return (
                  <tr key={v.codvend} className={"border-t border-black/5 " + (inativo ? "opacity-50" : "")} data-testid={`vin-row-${v.codvend}`}>
                    <td className="py-2 font-medium">{v.codvend}</td>
                    <td className="py-2">
                      <span className={inativo ? "line-through" : ""}>{v.nome_sankhya}</span>
                      {inativo && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">desativado</span>}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={v.user_id || ""}
                          onChange={(e) => salvarVinculo(v.codvend, e.target.value)}
                          className={INPUT}
                          disabled={inativo}
                          data-testid={`vin-sel-${v.codvend}`}
                        >
                          <option value="">— não vinculado —</option>
                          {usuariosOrdenados.map((u) => (
                            <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
                          ))}
                        </select>
                        {sug && !v.user_id && !inativo && (
                          <button onClick={() => salvarVinculo(v.codvend, sug.id)}
                            data-testid={`vin-sug-${v.codvend}`}
                            className="text-xs text-brand font-medium hover:underline">
                            Sugestão: {sug.name}
                          </button>
                        )}
                        {v.user_role && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{v.user_role}</span>}
                      </div>
                    </td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditVend({ codvend: v.codvend, nome_sankhya: v.nome_sankhya })}
                          data-testid={`vin-edit-${v.codvend}`}
                          title="Editar nome"
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-input text-slate-600 hover:bg-slate-50">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleAtivo(v)}
                          data-testid={`vin-toggle-${v.codvend}`}
                          title={inativo ? "Reativar" : "Desativar"}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-input text-slate-600 hover:bg-slate-50">
                          {inativo ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Modal Novo vendedor */}
      <Dialog open={!!novoVend} onOpenChange={(o) => !o && setNovoVend(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar vendedor do Sankhya</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={LABEL}>CODVEND</label>
              <input type="number" value={novoVend?.codvend || ""}
                onChange={(e) => setNovoVend({ ...novoVend, codvend: e.target.value })}
                className={INPUT + " w-full"} data-testid="novo-vend-codvend" />
            </div>
            <div>
              <label className={LABEL}>Nome (como consta no Sankhya)</label>
              <input value={novoVend?.nome || ""}
                onChange={(e) => setNovoVend({ ...novoVend, nome: e.target.value })}
                className={INPUT + " w-full"} data-testid="novo-vend-nome" />
            </div>
            <p className="text-[11px] text-muted-foreground">CODVEND repetido é recusado. Um vendedor desativado permanece no histórico e pode ser reativado.</p>
          </div>
          <DialogFooter>
            <button onClick={criarVendedor} data-testid="novo-vend-salvar"
              className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover">
              Adicionar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar nome */}
      <Dialog open={!!editVend} onOpenChange={(o) => !o && setEditVend(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar nome do vendedor · CODVEND {editVend?.codvend}</DialogTitle></DialogHeader>
          <div>
            <label className={LABEL}>Nome (como consta no Sankhya)</label>
            <input value={editVend?.nome_sankhya || ""}
              onChange={(e) => setEditVend({ ...editVend, nome_sankhya: e.target.value })}
              className={INPUT + " w-full"} data-testid="edit-vend-nome" />
          </div>
          <DialogFooter>
            <button onClick={salvarNome} data-testid="edit-vend-salvar"
              className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover">
              Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResBox({ rot, v, n, destaque }) {
  return (
    <div className={`rounded-xl border ${destaque ? "border-brand/40 bg-brand-soft" : "border-black/5 bg-white"} p-4`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{rot}</div>
      <div className={`text-2xl font-semibold ${destaque ? "text-brand-hover" : "text-navy"} mt-1`}>{brl(v)}</div>
      {n != null && <div className="text-xs text-muted-foreground mt-1">{n === 1 ? "1 nota" : `${n} notas`}</div>}
    </div>
  );
}

function TabelaTop({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground">
          <tr><th className="text-left py-2">TOP</th><th className="text-left py-2">Tipo</th><th className="text-right py-2">Notas</th><th className="text-right py-2">Valor</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.codtipoper} className="border-t border-black/5">
              <td className="py-2 font-medium">{r.codtipoper}</td>
              <td className="py-2 capitalize text-muted-foreground">{r.tipo}</td>
              <td className="py-2 text-right">{r.num_notas}</td>
              <td className="py-2 text-right font-semibold">{brl(r.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Editor de feriados extras (municipais/estaduais). Os nacionais fixos e móveis
// (Carnaval, Sexta-Santa, Corpus Christi) são calculados automaticamente e não
// aparecem aqui — apenas o que precisa ser adicionado localmente.
function Feriados() {
  const [itens, setItens] = useState(null);
  const [data, setData] = useState("");
  const [nome, setNome] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const { data: r } = await api.get("/sankhya/feriados");
      setItens(r.itens || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, []);

  const adicionar = async () => {
    if (!data || !nome.trim()) { toast.error("Preencha data e nome do feriado."); return; }
    setSalvando(true);
    try {
      await api.post("/sankhya/feriados", { data, nome: nome.trim() });
      setData(""); setNome("");
      await carregar();
      toast.success("Feriado adicionado.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSalvando(false); }
  };
  const remover = async (d) => {
    try {
      await api.delete(`/sankhya/feriados/${d}`);
      await carregar();
      toast.success("Feriado removido.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className={CARD} data-testid="sankhya-feriados">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays className="w-4 h-4 text-brand" />
        <h3 className="font-semibold text-navy">Feriados</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Nacionais fixos e móveis (Carnaval, Sexta-Santa, Corpus Christi) são calculados automaticamente. Adicione aqui feriados municipais e estaduais que valem para a MPires.
      </p>
      <div className="grid gap-2 sm:grid-cols-[160px_1fr_auto] items-end">
        <div>
          <label className={LABEL}>Data</label>
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="feriado-data" className={INPUT + " w-full"} />
        </div>
        <div>
          <label className={LABEL}>Nome do feriado</label>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Aniversário da cidade" data-testid="feriado-nome" className={INPUT + " w-full"} maxLength={120} />
        </div>
        <button onClick={adicionar} disabled={salvando} data-testid="btn-add-feriado"
          className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
          {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Adicionar
        </button>
      </div>

      <div className="mt-4">
        {carregando ? (
          <div className="text-xs text-muted-foreground">Carregando…</div>
        ) : itens && itens.length ? (
          <ul className="divide-y divide-black/5 rounded-lg border border-black/5 overflow-hidden">
            {itens.map((f) => (
              <li key={f.data} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`feriado-row-${f.data}`}>
                <span className="w-24 font-medium text-navy tabular-nums">{f.data.split("-").reverse().join("/")}</span>
                <span className="flex-1 text-slate-700 truncate">{f.nome}</span>
                <button onClick={() => remover(f.data)} data-testid={`btn-del-feriado-${f.data}`}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-rose-600 hover:bg-rose-50 text-xs font-medium">
                  <Trash2 className="w-3.5 h-3.5" /> Remover
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs text-muted-foreground">Nenhum feriado extra cadastrado.</div>
        )}
      </div>
    </section>
  );
}
